/**
 * Push notification service for GEMS Live.
 * Handles service worker registration, push subscription, and notification permissions.
 */

import axios from 'axios';
import { API_BASE_URL } from '../config';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

export async function registerPushNotifications(api) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.log('[Push] Not supported in this browser');
    return false;
  }

  if (!VAPID_PUBLIC_KEY) {
    console.warn('[Push] VAPID_PUBLIC_KEY not configured');
    return false;
  }

  try {
    // Register the push service worker
    console.log('[Push] Registering service worker...');
    const registration = await navigator.serviceWorker.register('/sw-push.js');
    await navigator.serviceWorker.ready;
    console.log('[Push] Service worker ready');

    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      console.log('[Push] No existing subscription, requesting permission...');
      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('[Push] Permission denied by user');
        return false;
      }
      console.log('[Push] Permission granted, subscribing...');

      // Subscribe
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      console.log('[Push] Subscription created');
    } else {
      console.log('[Push] Using existing subscription');
    }

    // Validate subscription object before sending
    const subJson = subscription.toJSON();
    if (!subJson.endpoint || !subJson.keys?.p256dh || !subJson.keys?.auth) {
      console.error('[Push] Invalid subscription object:', subJson);
      return false;
    }
    
    console.log('[Push] Subscription endpoint:', subJson.endpoint.substring(0, 60) + '...');
    console.log('[Push] p256dh length:', subJson.keys.p256dh.length);
    console.log('[Push] auth length:', subJson.keys.auth.length);

    // Send subscription to backend
    const response = await api.post('/push/subscribe', {
      subscription: subJson,
    });

    console.log('[Push] Registration successful:', response.data);
    return true;
  } catch (error) {
    console.error('[Push] Registration error:', error);
    return false;
  }
}

export async function unregisterPushNotifications(api) {
  try {
    const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
    if (registration) {
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await api.post('/push/unsubscribe', { endpoint: subscription.endpoint });
        await subscription.unsubscribe();
        console.log('[Push] Unsubscribed successfully');
      }
    }
    return true;
  } catch (error) {
    console.error('[Push] Unsubscription error:', error);
    return false;
  }
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function getPushPermissionState() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'granted', 'denied', 'default'
}

export async function checkPushStatus(api) {
  try {
    const response = await api.get('/push/status');
    return response.data;
  } catch (error) {
    console.error('[Push] Status check error:', error);
    return null;
  }
}

export async function sendTestPush(api) {
  try {
    const response = await api.post('/push/test');
    return response.data;
  } catch (error) {
    console.error('[Push] Test push error:', error);
    throw error;
  }
}

export async function trackNotificationEvent(notificationId, event, metadata = {}) {
  try {
    await axios.post(`${API_BASE_URL}/api/notifications/${notificationId}/track`, {
      event,
      ...metadata,
    });
  } catch { /* best-effort */ }
}
